import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { School, GraduationCap, UserCircle, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeadTeacherLogin = () => {
  const navigate = useNavigate();
  const { login, completeLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Trim to eliminate accidental leading/trailing whitespace from copy-paste.
      const cleanUsername = (loginData.username || '').trim();
      const cleanPassword = (loginData.password || '').trim();
      const user = await login(cleanUsername, cleanPassword);
      
      if (user.role !== 'head_teacher') {
        toast.error('This login is for Head Teacher/Principal only.');
        return;
      }
      
      if (user.must_change_password) {
        setPasswordForm({ ...passwordForm, currentPassword: cleanPassword });
        setShowPasswordChange(true);
        return;
      }
      
      toast.success('Welcome back, Principal!');
      navigate('/head-teacher/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword === passwordForm.currentPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully! Welcome!');
      setShowPasswordChange(false);
      await completeLogin();
      navigate('/head-teacher/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
              <School className="w-12 h-12 text-purple-600" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-purple-900" data-testid="head-teacher-login-title">
            Head Teacher Login
          </CardTitle>
          <CardDescription className="text-lg">
            MyClassAI - Administration Portal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                placeholder="Enter your username"
                required
                data-testid="head-teacher-username-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  data-testid="head-teacher-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full py-6 text-lg bg-purple-600 hover:bg-purple-700" disabled={loading} data-testid="head-teacher-login-button">
              {loading ? 'Logging in...' : 'Login as Head Teacher'}
            </Button>
          </form>

          <div className="mt-6 space-y-2">
            <Link to="/teacher-login">
              <Button variant="outline" className="w-full">
                <GraduationCap className="w-4 h-4 mr-2" />
                Teacher Login
              </Button>
            </Link>
            <Link to="/student-login">
              <Button variant="outline" className="w-full">
                <UserCircle className="w-4 h-4 mr-2" />
                Student Login
              </Button>
            </Link>
          </div>

          <div className="mt-6 p-4 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-800 font-semibold mb-2">Demo Credentials:</p>
            <p className="text-xs text-purple-700">Username: headteacher</p>
            <p className="text-xs text-purple-700">Password: password123</p>
          </div>
        </CardContent>
      </Card>

      {/* Mandatory Password Change Modal */}
      <Dialog open={showPasswordChange} onOpenChange={() => {}}>
        <DialogContent className="bg-white border-purple-200 max-w-md max-h-[90vh] overflow-y-auto [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex justify-center mb-3">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                <Lock className="w-7 h-7 text-amber-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">Change Your Password</DialogTitle>
            <DialogDescription className="text-center">
              For security, you must set a new password before continuing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password *</Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={handleChangePassword} 
              disabled={changingPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="change-password-btn"
            >
              {changingPassword ? 'Changing Password...' : 'Set New Password & Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeadTeacherLogin;
